(function () {
  if (typeof customElements === 'undefined' || customElements.get('menasa-gallery')) return;

  customElements.define('menasa-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.abort || !window.MenasaGalleryMap) return;
      this.abort = new AbortController();
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
      this.empty = this.querySelector('[data-gallery-empty]');
      this.status = this.querySelector('[data-gallery-status]');
      this.visibleSlides = this.slides.slice();
      this.index = 0;
      this.autoplayMs = Math.max(0, Number(this.dataset.autoplayMs) || 0);
      this.manualStopped = false;
      this.isVisible = true;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (!this.track || !this.slides.length) return;

      const options = { signal: this.abort.signal };
      this.root = this.closest('[data-menasa-product-scope]') || this.parentElement;
      this.variants = this.readVariants();
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button) return;
        this.stopAuto();
        if (button.hasAttribute('data-gallery-prev')) this.show(this.index - 1);
        if (button.hasAttribute('data-gallery-next')) this.show(this.index + 1);
        if (button.hasAttribute('data-gallery-dot')) this.showById(button.dataset.mediaId);
      }, options);
      this.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.stopAuto();
        this.revealControls();
        this.show(this.index + (event.key === 'ArrowLeft' ? -1 : 1));
      }, options);
      this.addEventListener('pointerenter', () => {
        this.clearAuto();
        this.revealControls();
        this.scheduleControls();
      }, options);
      this.addEventListener('pointerleave', () => {
        this.scheduleControls();
        this.scheduleAuto();
      }, options);
      this.addEventListener('focusin', () => {
        this.clearAuto();
        this.revealControls();
        this.scheduleControls();
      }, options);
      this.addEventListener('focusout', event => {
        if (!this.contains(event.relatedTarget)) {
          this.scheduleControls();
          this.scheduleAuto();
        }
      }, options);
      this.addEventListener('touchstart', event => {
        this.stopAuto();
        this.touchX = event.touches[0].clientX;
        this.revealControls();
      }, { signal: this.abort.signal, passive: true });
      this.addEventListener('touchend', event => {
        if (this.touchX == null) return;
        const delta = event.changedTouches[0].clientX - this.touchX;
        this.touchX = null;
        if (Math.abs(delta) > 40) this.show(this.index + (delta < 0 ? 1 : -1));
        this.scheduleControls();
      }, { signal: this.abort.signal, passive: true });
      this.addEventListener('touchcancel', () => { this.touchX = null; }, options);
      document.addEventListener('visibilitychange', () => this.scheduleAuto(), options);
      this.reducedMotion.addEventListener('change', () => this.scheduleAuto(), options);
      if (typeof IntersectionObserver !== 'undefined') {
        this.observer = new IntersectionObserver(entries => {
          this.isVisible = entries[0]?.isIntersecting && entries[0].intersectionRatio >= .25;
          this.scheduleAuto();
        }, { threshold: [.25] });
        this.observer.observe(this);
      }
      if (this.root) this.root.addEventListener('menasa:media', event => {
        this.stopAuto();
        this.applyColour(event.detail || {});
      }, options);
      this.show(0, false);
      this.applyInitialSelection();
      this.scheduleAuto();
    }

    applyInitialSelection() {
      if (!this.root) return;
      const colour = this.root.querySelector('[data-option-kind="colour"] input:checked')?.value || '';
      const selected = this.root.querySelector('[data-variant-select] option:checked');
      if (colour) this.applyColour({ colour: colour, imageId: selected?.dataset.imageId || '' });
    }

    readVariants() {
      const source = this.querySelector('[data-gallery-variants]');
      if (!source) return [];
      try { return JSON.parse(source.textContent); } catch (_error) { return []; }
    }

    applyColour(detail) {
      const map = window.MenasaGalleryMap.resolve(this.dataset.productHandle, detail.colour, this.dataset.colourMap, this.variants);
      if (!map.known) {
        this.setVisible(this.slides);
        this.filterAssociatedMedia(new Set(), new Set(), false);
        if (detail.imageId) this.showById(detail.imageId);
        return;
      }
      const names = new Set(map.filenames);
      const ids = new Set(map.mediaIds.map(String));
      const matches = this.slides.filter(slide => names.has(slide.dataset.filename) || ids.has(slide.dataset.mediaId));
      this.setVisible(matches, detail.colour);
      this.filterAssociatedMedia(names, ids, true);
    }

    filterAssociatedMedia(names, ids, strict) {
      if (!this.root) return;
      this.root.querySelectorAll('[data-gallery-associated]').forEach(element => {
        const match = names.has(element.dataset.filename) || ids.has(element.dataset.mediaId);
        element.hidden = strict && !match;
      });
    }

    setVisible(slides, colour) {
      this.visibleSlides = slides;
      const visibleIds = new Set(slides.map(slide => slide.dataset.mediaId));
      this.slides.forEach(slide => { slide.hidden = !visibleIds.has(slide.dataset.mediaId); });
      this.dots.forEach(dot => { dot.hidden = !visibleIds.has(dot.dataset.mediaId); });
      const isEmpty = !slides.length;
      this.classList.toggle('is-empty', isEmpty);
      this.track.hidden = isEmpty;
      if (this.empty) {
        this.empty.hidden = !isEmpty;
        const label = this.empty.querySelector('[data-gallery-empty-colour]');
        if (label) label.textContent = colour || '';
      }
      this.querySelectorAll('[data-gallery-prev], [data-gallery-next], [data-gallery-indicators]').forEach(control => {
        control.hidden = slides.length < 2;
      });
      this.index = 0;
      if (!isEmpty) this.show(0);
      else if (this.status) this.status.textContent = (colour || 'Selected colour') + ' imagery is not available yet.';
    }

    showById(imageId) {
      const index = this.visibleSlides.findIndex(slide => slide.dataset.mediaId === String(imageId));
      if (index >= 0) this.show(index);
    }

    show(index, announce = true) {
      if (!this.visibleSlides.length) return;
      this.index = (index + this.visibleSlides.length) % this.visibleSlides.length;
      const active = this.visibleSlides[this.index];
      this.visibleSlides.forEach(slide => { slide.classList.toggle('is-active', slide === active); });
      this.track.style.transform = 'translate3d(' + (-this.index * 100) + '%, 0, 0)';
      this.dots.forEach(dot => {
        const selected = dot.dataset.mediaId === active.dataset.mediaId;
        dot.classList.toggle('is-active', selected);
        dot.setAttribute('aria-pressed', String(selected));
      });
      if (announce && this.status) this.status.textContent = 'Image ' + (this.index + 1) + ' of ' + this.visibleSlides.length;
      if (announce) {
        this.revealControls();
        this.scheduleControls();
      }
    }

    revealControls() {
      clearTimeout(this.controlsTimer);
      this.classList.add('is-controls-visible');
    }

    scheduleControls() {
      clearTimeout(this.controlsTimer);
      this.controlsTimer = setTimeout(() => {
        this.classList.remove('is-controls-visible');
      }, 1800);
    }

    clearAuto() {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    stopAuto() {
      this.manualStopped = true;
      this.clearAuto();
    }

    scheduleAuto() {
      this.clearAuto();
      if (!this.autoplayMs || this.manualStopped || !this.isVisible || document.hidden || this.reducedMotion.matches || this.matches(':hover') || this.matches(':focus-within') || this.visibleSlides.length < 2) return;
      this.autoplayTimer = setTimeout(() => {
        this.show(this.index + 1, false);
        this.revealControls();
        this.scheduleControls();
        this.scheduleAuto();
      }, this.autoplayMs);
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      clearTimeout(this.controlsTimer);
      this.clearAuto();
      if (this.observer) this.observer.disconnect();
      this.observer = null;
      this.abort = null;
    }
  });
}());
